import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BaseComponent } from '@macs-frontend/core';
import { State } from '@macs-frontend/reducers';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { SubheaderTabComponent } from '../subheader-tab/subheader-tab.component';

export interface SubheaderTabChangeEventModel {
  index: number;
  tab: SubheaderTabComponent;
}

@Component({
  selector: 'md-subheader-tabs',
  templateUrl: 'subheader-tabs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubheaderTabsComponent
  extends BaseComponent
  implements AfterContentInit, OnDestroy
{
  // @ts-expect-error
  @Input() tabsClasses: string;
  @Input() handleClick = false;
  @Input() defaultTabIndex = 0;

  @Output()
  tabChange: EventEmitter<SubheaderTabChangeEventModel> = new EventEmitter<SubheaderTabChangeEventModel>();

  @ContentChildren(SubheaderTabComponent)
  tabs?: QueryList<SubheaderTabComponent>;
  // @ts-expect-error
  @ViewChild('line', { static: true }) line: ElementRef;

  private intersectionObserverOptions: IntersectionObserverInit = {
    threshold: 0.01,
  };
  private mutationObserverOptions: MutationObserverInit = {
    subtree: true,
    characterData: true,
  };
  private intersectionObserver: IntersectionObserver;
  private mutationObserver: MutationObserver;
  // @ts-expect-error
  activeTab: SubheaderTabComponent;

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private host: ElementRef,
    readonly cdr: ChangeDetectorRef,
    readonly store$: Store<State>,
  ) {
    super();
    router.events.pipe(takeUntil(this.destroy$)).subscribe(s => {
      if (s instanceof NavigationEnd) {
        setTimeout(() => cdr.markForCheck());
      }
    });

    this.intersectionObserver = new IntersectionObserver(
      this.refreshLine.bind(this),
      this.intersectionObserverOptions,
    );
    this.mutationObserver = new MutationObserver(this.refreshLine.bind(this));

    this.intersectionObserver.observe(this.host.nativeElement);
    this.mutationObserver.observe(this.host.nativeElement, this.mutationObserverOptions);
  }

  ngAfterContentInit() {
    this.tabs?.forEach((tab, i) => {
      if (tab.active) {
        this.setActiveTab(tab, i);
      }
      tab.activeChange.pipe(takeUntil(this.destroy$)).subscribe((isActive: boolean) => {
        if (isActive) {
          this.resetTabs(tab);
          this.setActiveTab(tab, i);
        }
      });

      if (this.handleClick) {
        tab.tabClick.pipe(takeUntil(this.destroy$)).subscribe((e: MouseEvent) => {
          this.resetTabs();
          tab.active = true;
        });
      }
    });

    if (!this.tabs?.some(tab => tab.active)) {
      const defaultTab = this.tabs?.toArray()[this.defaultTabIndex];
      if (defaultTab) {
        defaultTab.active = true;
      }
    }

    setTimeout(() => {
      if (this.activeTab) {
        this.setLine(this.activeTab);
      }
    }, 500);
  }

  refreshLine() {
    if (this.activeTab) {
      this.setLine(this.activeTab);
    }
  }

  resetTabs(except?: SubheaderTabComponent) {
    this.tabs?.forEach(tab => {
      if (!except || tab !== except) {
        tab.active = false;
      }
    });
  }

  setLine(tab: SubheaderTabComponent) {
    const dimensions = tab.getDimensions();
    this.renderer.setStyle(
      this.line.nativeElement,
      'transform',
      `translateX(${dimensions.left}px)`,
    );
    this.renderer.setStyle(this.line.nativeElement, 'width', `${dimensions.width}px`);
  }

  private setActiveTab(tab: SubheaderTabComponent, index: number) {
    this.tabChange.emit({
      index,
      tab,
    });
    this.activeTab = tab;
    this.setLine(tab);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.intersectionObserver.disconnect();
    this.mutationObserver.disconnect();
  }
}
